import React, { useMemo, useState, useEffect } from "react";
import { Dropdown, Spinner, Row, Col } from "react-bootstrap";
import { Drawer, Form, Input, Select, Button as AntButton } from "antd";
import { ExpandOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import IconButton from "components/common/IconButton";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useMeetingPoints from "hooks/airline/useMeetingPoints";

const statusStyle = (active) =>
  active
    ? { bg: "#E6F9F0", text: "#059669", label: "Active" }
    : { bg: "#FDECEC", text: "#DC2626", label: "Inactive" };

const DetailRow = ({ label, value, isLast = false }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "170px 1fr",
      gap: 10,
      paddingBottom: isLast ? 0 : 10,
      marginBottom: isLast ? 0 : 10,
      borderBottom: isLast ? "none" : "1px solid #f1f3f5",
    }}
  >
    <span className="text-muted" style={{ fontSize: 12 }}>{label}</span>
    <span style={{ fontSize: 13, color: "#1f2937", wordBreak: "break-word" }}>{value || "—"}</span>
  </div>
);

// --- DETAILS DRAWER ---
const MeetingPointDetailsDrawer = ({ visible, item, onClose, onEdit }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  useEffect(() => { if (!visible) setIsExpanded(false); }, [visible]);

  const ui = statusStyle(item?.status === true);

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      placement="right"
      size={isExpanded ? "large" : 600}
      zIndex={2000}
      title={null}
      closable={false}
      destroyOnHidden
    >
      <div style={{ maxWidth: isExpanded ? "100%" : 700, margin: "0 auto", fontFamily: "Inter, sans-serif" }}>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            <span>Airline Management</span>
            <span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>{item?.name || "Details"}</span>
          </div>
          <div style={{ display: "flex", gap: 14 }}>
            <ExpandOutlined style={{ cursor: "pointer" }} onClick={() => setIsExpanded(prev => !prev)} />
            <EditOutlined style={{ cursor: "pointer" }} onClick={() => onEdit?.(item)} />
          </div>
        </div>
        <h5 className="mb-3">{item?.name}</h5>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <span className="d-inline-flex align-items-center px-2 py-1 rounded-pill fw-medium" style={{ backgroundColor: "#EEF2FF", color: "#4F46E5", fontSize: "0.75rem" }}>
            {item?.code || "—"}
          </span>
          <span className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-medium" style={{ backgroundColor: ui.bg, color: ui.text, fontSize: "0.75rem" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: ui.text }} />
            {ui.label}
          </span>
        </div>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, background: "#fff", padding: 14, marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 10, textTransform: "uppercase" }}>Details</div>
          <DetailRow label="Code" value={item?.code} />
          <DetailRow label="Name" value={item?.name} />
          <DetailRow label="Description" value={item?.description} />
          <DetailRow label="ID" value={item?.id} isLast />
        </div>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, background: "#fafafa", padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 10, textTransform: "uppercase" }}>Audit</div>
          <DetailRow label="Created By" value={item?.createdBy} />
          <DetailRow label="Created At" value={item?.createdAt ? dayjs(item.createdAt).format("MMM D, YYYY HH:mm") : "—"} />
          <DetailRow label="Updated By" value={item?.updatedBy} />
          <DetailRow label="Updated At" value={item?.updatedAt ? dayjs(item.updatedAt).format("MMM D, YYYY HH:mm") : "—"} isLast />
        </div>
      </div>
    </Drawer>
  );
};

// --- FORM DRAWER ---
const MeetingPointFormDrawer = ({ open, onClose, mode = "create", initialValues = null, loading, onSubmit }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [form] = Form.useForm();
  const isEditMode = mode === "edit";

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        code: initialValues?.code || "",
        name: initialValues?.name || "",
        description: initialValues?.description || "",
        status: initialValues?.status === false ? "inactive" : "active",
      });
    }
  }, [open, initialValues, form]);

  const onFinish = async (values) => {
    await onSubmit({
      ...values,
      code: values.code.trim().toUpperCase(),
      status: values.status !== "inactive"
    });
    form.resetFields();
    onClose();
  };

  return (
    <Drawer
      zIndex={2000}
      placement="right"
      closable={false}
      size={isExpanded ? "large" : 600}
      onClose={onClose}
      open={open}
      destroyOnHidden
      footer={
        <div style={{ textAlign: "right", padding: "10px 0" }}>
          <AntButton className="me-2" onClick={onClose}>Cancel</AntButton>
          <AntButton type="primary" onClick={() => form.submit()} loading={loading}>
            {isEditMode ? "Save Changes" : "Create Meeting Point"}
          </AntButton>
        </div>
      }
    >
      <div style={{ maxWidth: isExpanded ? "100%" : 700, margin: "0 auto" }}>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div style={{ fontSize: 15, color: "#6b7280" }}>
            <span>Airline Management</span>
            <span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>{isEditMode ? "Edit Meeting Point" : "Create Meeting Point"}</span>
          </div>
          <ExpandOutlined style={{ cursor: "pointer" }} onClick={() => setIsExpanded(prev => !prev)} />
        </div>
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Form.Item label="Code" name="code" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. TERMINAL_1" />
          </Form.Item>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Terminal 1 Arrival" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} placeholder="Describe the meeting point..." />
          </Form.Item>
          <Form.Item label="Status" name="status">
            <Select options={[{ label: "Active", value: "active" }, { label: "Inactive", value: "inactive" }]} />
          </Form.Item>
        </Form>
      </div>
    </Drawer>
  );
};

// --- MAIN COMPONENT ---
export default function MeetingPoints() {
  const { meetingPoints, loading, createMeetingPoint, updateMeetingPoint, deleteMeetingPoint } = useMeetingPoints();

  const [searchTerm, setSearchTerm] = useState("");
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showFormDrawer, setShowFormDrawer] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const filteredData = useMemo(() => {
    return (meetingPoints || []).filter(item => 
      `${item.name} ${item.code}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [meetingPoints, searchTerm]);

  const columns = useMemo(() => [
    { accessorKey: "code", header: "Code", meta: { headerProps: { className: "text-900" } } },
    { accessorKey: "name", header: "Name", meta: { headerProps: { className: "text-900" } } },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ getValue }) => getValue() || "-",
      meta: { headerProps: { className: "text-900" } },
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row) => row.status === true,
      cell: ({ getValue }) => {
        const ui = statusStyle(getValue());
        return (
          <span className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-medium" style={{ backgroundColor: ui.bg, color: ui.text, fontSize: "0.75rem" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: ui.text }} />
            {ui.label}
          </span>
        );
      },
      meta: { headerProps: { className: "text-900" } },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <Dropdown className="action-dropdown p-0">
            <Dropdown.Toggle as="button" className="bg-transparent border-0 p-0 shadow-none text-secondary">⋯</Dropdown.Toggle>
            <Dropdown.Menu className="action-menu">
              <Dropdown.Item onClick={() => { setSelectedItem(item); setShowDetailsDrawer(true); }}>View</Dropdown.Item>
              <Dropdown.Item onClick={() => { setFormMode("edit"); setSelectedItem(item); setShowFormDrawer(true); }}>Edit</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item className="text-danger" onClick={() => { setItemToDelete(item); setShowDeleteModal(true); }}>Delete</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        );
      },
      meta: { headerProps: { className: "text-900 text-center" }, cellProps: { className: "text-center" } },
    },
  ], []);

  const table = useAdvanceTable({
    data: filteredData,
    columns,
    sortable: true,
    pagination: true,
    perPage: 10,
  });

  // Center Loader Aligned with Affiliations
  if (loading && (!meetingPoints || meetingPoints.length === 0)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 240 }}>
        <Spinner animation="border" variant="success" />
      </div>
    );
  }

  return (
    <>
      <WidgetSectionTitle 
        icon="map-marker-alt" 
        title="TravoTask Meeting Points" 
        subtitle="Manage airport meeting points used for traveler pickups." 
        className="mb-4 mt-3" 
      />

      {/* ================= ADD BUTTON ROW ================= */}
      <Row className="mb-3">
        <Col className="d-flex justify-content-end">
          <IconButton
            variant="falcon-default"
            size="sm"
            icon="plus"
            transform="shrink-3"
            onClick={() => { setFormMode("create"); setSelectedItem(null); setShowFormDrawer(true); }}
            style={{ backgroundColor: "#10b981", color: "#fff", borderColor: "#10b981", fontWeight: 600 }}
          >
            <span className="d-none d-sm-inline-block ms-1">Create Meeting Point</span>
          </IconButton>
        </Col>
      </Row>

      {/* ================= SEARCH + RESULTS ROW ================= */}
      <Row className="mb-2 g-3 align-items-center">
        <Col md={8} lg={9}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Input
              prefix={<span style={{ fontSize: 14 }}>🔍</span>}
              placeholder="Search by name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              size="large"
              style={{ borderRadius: 12, height: 30, flex: "1 1 300px", maxWidth: 300 }}
            />
          </div>
        </Col>
        <Col md={4} lg={3} className="text-end">
          <div style={{ fontSize: 13, color: "#64748b" }}>
            Results: <span className="fw-semibold text-dark">{filteredData.length}</span> of <span className="fw-semibold text-dark">{meetingPoints.length}</span>
          </div>
        </Col>
      </Row>

      {/* ================= TABLE ================= */}
      <AdvanceTableProvider {...table}>
        <AdvanceTable
          headerClassName="bg-light text-nowrap align-middle text-dark"
          rowClassName="align-middle white-space-nowrap"
          tableProps={{ bordered: true, striped: true, className: "fs-10 mb-0 overflow-hidden" }}
        />
        <div className="mt-3">
          <TablePaginationFooter
            page={table.getState().pagination.pageIndex}
            totalPages={table.getPageCount()}
            totalElements={filteredData.length}
            onPrev={() => table.previousPage()}
            onNext={() => table.nextPage()}
            pageSize={table.getState().pagination.pageSize}
            onPageSizeChange={(size) => table.setPageSize(size)}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        </div>
      </AdvanceTableProvider>

      <MeetingPointFormDrawer 
        open={showFormDrawer} 
        onClose={() => setShowFormDrawer(false)} 
        mode={formMode} 
        initialValues={selectedItem} 
        loading={loading} 
        onSubmit={(payload) => formMode === "edit" ? updateMeetingPoint(selectedItem.id, payload) : createMeetingPoint(payload)} 
      />

      <MeetingPointDetailsDrawer 
        visible={showDetailsDrawer} 
        item={selectedItem} 
        onClose={() => setShowDetailsDrawer(false)} 
        onEdit={(item) => { setShowDetailsDrawer(false); setFormMode("edit"); setSelectedItem(item); setShowFormDrawer(true); }} 
      />

      <ConfirmDeleteModal 
        show={showDeleteModal} 
        setShow={setShowDeleteModal} 
        itemName="meeting point" 
        loading={loading} 
        onConfirm={() => deleteMeetingPoint(itemToDelete?.id)} 
      />
    </>
  );
}